import React from 'react';
import Location from 'react-app-location';
import * as yup from 'yup';
import {
  DatabaseOutlined,
  DeleteOutlined,
  DiffOutlined,
  FieldTimeOutlined,
  FileSearchOutlined,
  MailOutlined,
  RocketOutlined,
  SearchOutlined,
  ToolOutlined,
  TrophyOutlined,
  UserOutlined,
} from '@ant-design/icons';

import AuditLogScreen from '@lib/pages/cstool/audit-log';
import CsToolInboxV2Screen from '@lib/pages/cstool/inbox/v2/lazy';
import {
  appRoutes as baseAppRoutes,
  AuditLogLocation,
  CsInboxHistoryLocation,
  CsInboxV2Location,
  CsSearchUserLocation,
  RouteItem,
} from '@lib/routes/routes';
import { UserRole } from '@lib/services/_api';

import BlueprintDataImportChangeScreen from '@pages/blueprint-data/import-change/lazy';
import BlueprintStructScreen from '@pages/blueprint-struct/lazy';
import BlueprintBulkDeleteVersionScreen from '@pages/blueprint-version-bulk-delete/lazy';
import CsToolArenaSegmentScreen from '@pages/cstool/arena-segment';
import DevelopAccountScreen from '@pages/cstool/develop-account';
import GauntletPointScreen from '@pages/cstool/gauntlet-point';
import GetCurrentProgressScreen from '@pages/cstool/get-current-progress';
import CsToolInboxHistoryScreen from '@pages/cstool/history';
import InboxBatchPlayersScreen from '@pages/cstool/inbox-batch-players';
import CsToolRecoveryAccountScreen from '@pages/cstool/recovery-account';
import ScanPlayersScreen from '@pages/cstool/scan-players';
import CsToolSearchUserScreen from '@pages/cstool/search-user/lazy';
import UpdateMessageScreen from '@pages/game-config/update-message/update-message';

export const ArenaSegmentLocation = new Location('/arena-segment');
export const DevelopAccountLocation = new Location('/develop-account');
export const CsRecoveryAccountLocation = new Location('/recovery-account');
export const CsGetCurrentProgressLocation = new Location('/get-current-progress');
export const GauntletPointLocation = new Location('/gauntlet-point');
export const ScanPlayersLocation = new Location('/scan-players');
export const InboxBatchPlayersLocation = new Location('/inbox-batch-players');
export const UpdateMessageLocation = new Location('/update-message');
export const BlueprintDataImportChangeLocation = new Location(
  '/blueprint/data/import-change',
  null,
  {
    version: yup.string(),
  },
);
export const BlueprintBulkDeleteVersionLocation = new Location('/blueprint/version/bulk-delete');

const excludeRoutes = ['odl-v2', 'gameConfig'];
const baseGameConfigRoutes = baseAppRoutes.find((item: RouteItem) => item.name === 'gameConfig');

if (baseGameConfigRoutes && baseGameConfigRoutes.children) {
  baseGameConfigRoutes.children.push({
    name: 'updateMessage',
    title: 'Update Message',
    icon: <MailOutlined />,
    component: UpdateMessageScreen,
    path: UpdateMessageLocation.path,
    exact: true,
    role: UserRole.Deployer,
  } as RouteItem);
}

const appRoutes: RouteItem[] = [
  ...baseAppRoutes.filter((item: RouteItem) => !excludeRoutes.includes(item.name)).slice(0, -1),
  ...(baseGameConfigRoutes ? [baseGameConfigRoutes] : []),
  {
    name: 'csTool',
    title: 'CS Tool',
    icon: <ToolOutlined />,
    children: [
      {
        name: 'searchUser',
        title: 'Search User',
        icon: <SearchOutlined />,
        component: CsToolSearchUserScreen,
        path: CsSearchUserLocation.path,
        exact: true,
        role: UserRole.CsViewer,
      },
      {
        name: 'auditLog',
        title: 'Audit Log',
        icon: <FieldTimeOutlined />,
        component: AuditLogScreen,
        path: AuditLogLocation.path,
        exact: true,
        role: UserRole.CsViewer,
      },
      {
        name: 'inboxV2',
        title: 'Inbox V2',
        icon: <MailOutlined />,
        component: CsToolInboxV2Screen,
        path: CsInboxV2Location.path,
        exact: true,
        role: UserRole.CsEditor,
      },
      {
        name: 'inboxBatchPlayers',
        title: 'Inbox Batch Players',
        icon: <MailOutlined />,
        component: InboxBatchPlayersScreen,
        path: InboxBatchPlayersLocation.path,
        exact: true,
        role: UserRole.CsEditor,
      },
      {
        name: 'inboxHistory',
        title: 'Inbox History',
        icon: <MailOutlined />,
        component: CsToolInboxHistoryScreen,
        path: CsInboxHistoryLocation.path,
        exact: true,
        hideInMenu: true,
        role: UserRole.CsViewer,
      },
      {
        name: 'arenaSegment',
        title: 'Arena Segment',
        icon: <DatabaseOutlined />,
        component: CsToolArenaSegmentScreen,
        path: ArenaSegmentLocation.path,
        exact: true,
        role: UserRole.CsViewer,
      },
      {
        name: 'developAccount',
        title: 'Develop Account',
        icon: <UserOutlined />,
        component: DevelopAccountScreen,
        path: DevelopAccountLocation.path,
        exact: true,
        role: UserRole.Admin,
      },
      {
        name: 'recoveryAccount',
        title: 'Recovery Account',
        icon: <UserOutlined />,
        component: CsToolRecoveryAccountScreen,
        path: CsRecoveryAccountLocation.path,
        exact: true,
        role: UserRole.CsEditor,
      },
      {
        name: 'getCurrentProgress',
        title: 'Get Lobby Progress',
        icon: <RocketOutlined />,
        component: GetCurrentProgressScreen,
        path: CsGetCurrentProgressLocation.path,
        exact: true,
        role: UserRole.CsEditor,
      },
      {
        name: 'gauntletPoint',
        title: 'Gauntlet Point',
        icon: <TrophyOutlined />,
        component: GauntletPointScreen,
        path: GauntletPointLocation.path,
        exact: true,
        role: UserRole.CsEditor,
      },
      {
        name: 'scanPlayers',
        title: 'Scan Players',
        icon: <FileSearchOutlined />,
        component: ScanPlayersScreen,
        path: ScanPlayersLocation.path,
        exact: true,
        role: UserRole.Admin,
      },
    ],
  },
];
const blueprintRoutes = appRoutes.find((item: RouteItem) => item.name === 'blueprint');
const blueprintStructScreen =
  blueprintRoutes?.children?.find((item: RouteItem) => item.name === 'blueprintProto') ||
  ({} as RouteItem);
blueprintStructScreen.component = BlueprintStructScreen;

if (blueprintRoutes && blueprintRoutes.children) {
  blueprintRoutes.children.push({
    name: 'blueprintImportChange',
    title: 'Import Change',
    icon: <DiffOutlined />,
    component: BlueprintDataImportChangeScreen,
    path: BlueprintDataImportChangeLocation.path,
    exact: true,
    role: UserRole.DataEditor,
  } as RouteItem);
  blueprintRoutes.children.push({
    name: 'blueprintBulkDeleteVersion',
    title: 'Bulk Delete Blueprint',
    icon: <DeleteOutlined />,
    component: BlueprintBulkDeleteVersionScreen,
    path: BlueprintBulkDeleteVersionLocation.path,
    exact: true,
    role: UserRole.Admin,
  } as RouteItem);
}

export { appRoutes };
